import React, { useState, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import { doc, onSnapshot, getDoc } from "firebase/firestore";
import { db, auth } from "../firebase";
import { useNotifications } from "../hooks/notificationsContext";
import getForexPrices from "../utils/getForexPrices";
import calculatePortfolio from "../utils/calculatePortfolio";

export default function BuySharesModal({ company, onClose, globalVars: initialGlobalVars, userData: initialUserData }) {
  const [companyState, setCompanyState] = useState(null);
  const [userState, setUserState] = useState(null);
  const [globalVars, setGlobalVars] = useState(initialGlobalVars);
  const [shares, setShares] = useState(0);
  const [denomination, setDenomination] = useState("USD");
  const [forexRates, setForexRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const { addNotification } = useNotifications();

  const userMoney = userState?.userMoney ?? 0;
  const CURRENCY_CODES = useMemo(() => ["AUD", "BRL", "CNY", "EUR", "ZAR"], []);

  const formatCurrency = (amount, code = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code === "USD" ? "USD" : code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount).replace(/[$\u00A0]/g, ""); 
  };

  useEffect(() => {
    setDenomination("USD");
  }, [company]);

  useEffect(() => {
    if (!company?.companyTicker) return;

    setLoading(true);
    const user = auth.currentUser;

    const companyRef = doc(db, "Companies", company.companyTicker);
    const unsubscribeCompany = onSnapshot(companyRef, (snap) => {
      if (snap.exists()) setCompanyState(snap.data());
    }, (err) => console.error("Company sync error:", err));

    let unsubscribeUser = () => {};
    if (user) {
      const userRef = doc(db, "Users", user.uid);
      unsubscribeUser = onSnapshot(userRef, (snap) => {
        if (snap.exists()) setUserState(snap.data());
      }, (err) => console.error("User sync error:", err));
    }

    const mainRef = doc(db, "Global_Vars", "Main");
    const unsubscribeMain = onSnapshot(mainRef, (snap) => {
      if (snap.exists()) setGlobalVars(snap.data());
    }, (err) => console.error("Global vars sync error:", err));

    const fetchForex = async () => {
      try {
        const promises = CURRENCY_CODES.map(ticker => getDoc(doc(db, "Companies", ticker)));
        const snapshots = await Promise.all(promises);
        const companiesArray = snapshots
          .filter(snap => snap.exists())
          .map(snap => ({
            companyTicker: snap.data().companyTicker,
            companyCurrency: snap.data().companyCurrency,
            companyStockPrice: snap.data().companyStockPrice,
          }));
        const ratesFromAPI = await getForexPrices(companiesArray);
        setForexRates(ratesFromAPI);
      } catch (err) {
        console.error("Forex array sync error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchForex();

    return () => {
      unsubscribeCompany();
      unsubscribeUser();
      unsubscribeMain();
    };
  }, [company, CURRENCY_CODES]);

  const balance = useMemo(() => {
    if (!userState) return 0;
    if (denomination === "USD") return userMoney ?? 0;
    return userState[`company${denomination}`] ?? 0;
  }, [userState, userMoney, denomination]);

  const unitLabel = useMemo(() => {
    if (!companyState) return "units";
    const tickerStr = String(companyState.companyTicker || "").toUpperCase().trim();
    if (CURRENCY_CODES.includes(tickerStr)) return "dollars";
    if (companyState.companyCrypto) return "coins";
    if (tickerStr === "XAU") return "units";
    if (["BND3", "BND7", "BND14"].includes(tickerStr)) return "bonds";
    return "shares";
  }, [companyState, CURRENCY_CODES]);

  const allowedWallets = useMemo(() => {
    if (!company) return ["USD"];
    
    const ticker = String(company.companyTicker || "").toUpperCase().trim();
    const region = company.companyRegion;
    let wallets = ["USD"];

    if (["AUD", "BRL", "CNY", "EUR", "ZAR"].includes(ticker)) {
      wallets = ["AUD", "BRL", "CNY", "EUR", "ZAR", "USD"].filter(c => c !== ticker);
    } 
    else if (ticker === "XAU") {
      wallets = ["USD", "AUD", "BRL", "CNY", "EUR", "ZAR"];
    } 
    else if (region && region !== "false") {
      const map = { AU: "AUD", SA: "BRL", AS: "CNY", EU: "EUR", AF: "ZAR", NA: "USD" };
      const regCur = map[region];
      if (regCur) {
        wallets.push(regCur);
      }
    }

    return [...new Set(wallets)];
  }, [company]);

  const totalCostData = useMemo(() => {
    if (!companyState || !userState) return { totalCost: 0, subtotal: 0, avgPrice: 0, isMember: false, discountAmount: 0 };
    
    const basePrice = Number(companyState.companyStockPrice) || 1;
    const tickerStr = String(companyState.companyTicker || "").toUpperCase().trim();
    const isMember = String(userState?.userCompanyTicker || "").toUpperCase().trim() === tickerStr;

    if (basePrice <= 0 || shares <= 0) {
      return { totalCost: 0, subtotal: 0, avgPrice: basePrice, isMember, discountAmount: 0 };
    }

    const rawCostUSD = basePrice * shares;
    let totalCostUSD = rawCostUSD;

    if (isMember) {
      totalCostUSD *= 0.97;
    }

    let totalCostInDenom = totalCostUSD;
    let subtotalInDenom = rawCostUSD;

    if (denomination !== "USD") {
      const targetRate = forexRates[denomination] || 1;
      totalCostInDenom = totalCostUSD / targetRate;
      subtotalInDenom = rawCostUSD / targetRate;
    }

    const discountAmount = subtotalInDenom - totalCostInDenom;

    return { 
      totalCost: totalCostInDenom, 
      subtotal: subtotalInDenom, 
      avgPrice: basePrice, 
      isMember, 
      discountAmount 
    };
  }, [companyState, shares, denomination, forexRates, userState]);

  const maxAffordableSharesByLinear = useMemo(() => {
    if (!companyState || !userState || balance <= 0) return 0;
    
    const basePrice = Number(companyState.companyStockPrice) || 1;
    const tickerStr = String(companyState.companyTicker || "").toUpperCase().trim();
    const isMember = String(userState?.userCompanyTicker || "").toUpperCase().trim() === tickerStr;
    let walletCapUSD = balance;
    if (denomination !== "USD") {
      const conversionFactor = forexRates[denomination] || 1;
      walletCapUSD = balance * conversionFactor;
    }

    const effectiveCashPoolUSD = isMember ? (walletCapUSD / 0.97) : walletCapUSD;

    if (basePrice <= 0) return 0;
    
    const calculatedMax = Math.floor(effectiveCashPoolUSD / basePrice);
    return isFinite(calculatedMax) ? Math.max(0, calculatedMax) : 0;
  }, [companyState, userState, balance, denomination, forexRates]);

  const sliderMax = useMemo(() => {
    const marketLimit = Number(companyState?.companyOutstanding || 0);
    return Math.min(Math.max(0, marketLimit), Math.max(0, maxAffordableSharesByLinear));
  }, [companyState, maxAffordableSharesByLinear]);

  useEffect(() => {
    if (shares > sliderMax) {
      setShares(sliderMax);
    }
  }, [sliderMax, shares]);

  const amountDue = totalCostData.totalCost;

  const handleTransactionBuy = async () => {
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      const userToken = await auth.currentUser.getIdToken();
      const res = await fetch("https://transactionbuy-5dhfwyjvzq-uc.a.run.app", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          Authorization: `Bearer ${userToken}` 
        },
        body: JSON.stringify({ 
          ticker: companyState?.companyTicker || "", 
          quantity: Number(shares) || 0,
          denomination: denomination
        }),
      });

      const data = await res.json();

      if (!data?.success) {
        setIsProcessing(false); 
        addNotification(data?.message || "Purchase failed.", "error");
        return;
      }

      addNotification(data.message, "success");
      onClose();
      await calculatePortfolio();

    } catch (err) {
      setIsProcessing(false);
      console.error("FATAL ERROR:", err);
      addNotification("Network error. Check connection.", "error");
    }
  };

  const ticker = String(companyState?.companyTicker || "").toUpperCase().trim();
  const isBond = ["BND3", "BND7", "BND14"].includes(ticker);
  const region = userState?.userRegion || ""; 

  let durationDays = isBond ? parseInt(ticker.slice(3)) : 0;
  let baseRate = Number(companyState?.currentInterestRate || 0);
  let price = Number(companyState?.companyStockPrice || 0);
  const shareCount = Number(shares) || 0;

  if (isBond) {
    if (region === "AF") {
      durationDays = Math.max(1, durationDays - 1); 
      baseRate = baseRate * 0.90; 
    } else if (region === "NA") {
      durationDays = durationDays + 1;
    }
  } else if (region === "AU") {
    baseRate = baseRate * 1.20;
  }

  const effectivePrice = (isBond && region === "AS") ? price * 2 : price;
  const principal = effectivePrice * shareCount;
  const globalTaxPercent = Number(globalVars?.globalBaseTaxRate || 0);
  const maturityDays = { BND3: 3, BND7: 7, BND14: 14 }[ticker] || 7;
  const dailyRatePercent = (Number(baseRate) * 10) / 100;

  const grossInterest = isBond ? (principal * dailyRatePercent * maturityDays) : 0;
  const taxAmount = grossInterest * (globalTaxPercent / 100);
  const netInterest = grossInterest - taxAmount;

  return ReactDOM.createPortal(
    <div style={{
      position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.85)", backdropFilter: "blur(5px)",
      display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000,
      fontFamily: "'JetBrains Mono', monospace"
    }}>
      <div style={{
        width: "440px", background: "rgba(0, 30, 30, 0.95)", border: "1px solid rgba(80, 227, 194, 0.3)",
        padding: "24px", position: "relative", boxShadow: "0 0 30px rgba(0,0,0,1)"
      }}>
        <h2 style={{ color: "#fff", margin: "0 0 20px 0", fontSize: "1.2rem", borderBottom: "1px solid rgba(80, 227, 194, 0.2)", paddingBottom: "10px" }}>
          Purchase - {companyState?.companyName}
        </h2>

        {loading ? (
          <div style={{ color: "#50e3c2", textAlign: "center", padding: "40px" }} className="blink">LOADING...</div>
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px", marginBottom: "20px" }}>
              <div>
                <label style={{ color: "#888", fontSize: "0.6rem", display: "block", marginBottom: "5px" }}>ORDER QUANTITY ({unitLabel})</label>
                <input 
                  type="number" 
                  value={shares} 
                  onChange={e => {
                    const requestedVal = Number(e.target.value);
                    setShares(Math.max(0, Math.min(sliderMax, requestedVal)));
                  }}
                  style={{ 
                    width: "100%", background: "rgba(0, 0, 0, 0.3)", border: "1px solid rgba(80, 227, 194, 0.2)", 
                    color: "#fff", padding: "10px", boxSizing: "border-box", outline: "none", fontFamily: "inherit" 
                  }}
                />
              </div>
              <div>
                <label style={{ color: "#888", fontSize: "0.6rem", display: "block", marginBottom: "5px" }}>WALLET</label>
                {allowedWallets.length > 1 ? (
                  <select
                    value={denomination}
                    onChange={(e) => {
                      setShares(0);
                      setDenomination(e.target.value);
                    }}
                    style={{
                      width: "100%", background: "rgba(0, 30, 30, 0.95)", border: "1px solid rgba(80, 227, 194, 0.3)",
                      color: "#50e3c2", padding: "9px", outline: "none", fontFamily: "inherit"
                    }}
                  >
                    {allowedWallets.map((code) => {
                      const bal = code === "USD" ? (userState?.userMoney ?? 0) : (userState?.[`company${code}`] ?? 0);
                      return (
                        <option key={code} value={code}>
                          {code} ({bal.toLocaleString()})
                        </option>
                      );
                    })}
                  </select>
                ) : (
                  <div style={{ 
                    width: "100%", background: "rgba(255, 255, 255, 0.02)", border: "1px solid rgba(255, 255, 255, 0.05)", 
                    color: amountDue > balance ? "#ff7b72" : "#50e3c2", padding: "10px", fontSize: "0.85rem" 
                  }}>
                    {denomination} ({balance.toLocaleString()})
                  </div>
                )}
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <input 
                type="range" 
                min="0" 
                max={sliderMax}
                value={shares} 
                onChange={(e) => setShares(Number(e.target.value))}
                style={{ width: "100%", accentColor: "#50e3c2" }}
              />
            </div>

            <div style={{ background: "rgba(0,0,0,0.3)", padding: "15px", marginBottom: "20px", border: "1px solid rgba(255,255,255,0.05)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px", fontSize: "0.7rem" }}>
                <span style={{ color: "#888" }}>RATE:</span>
                <span style={{ color: "#eee" }}>
                  1 {ticker} = USD ${Number(companyState?.companyStockPrice || 0).toFixed(4)}
                </span>
              </div>

              {isBond && shares > 0 && (
                <div style={{ 
                  marginTop: "12px", padding: "10px", background: "rgba(80, 227, 194, 0.05)",
                  border: "1px solid rgba(80, 227, 194, 0.15)", borderRadius: "2px"
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px", fontSize: "0.7rem" }}>
                    <span style={{ color: "#888" }}>MATURITY TERM:</span>
                    <span style={{ color: "#fff" }}>{durationDays} DAYS</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", fontSize: "0.7rem" }}>
                    <span style={{ color: "#888" }}>FIXED YIELD:</span>
                    <span style={{ color: "#50e3c2" }}>{(baseRate * 10).toFixed(2)}%</span>
                  </div>
                  <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: "8px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.7rem", fontWeight: "bold" }}>
                      <span style={{ color: "#888" }}>EST. NET PROFIT:</span>
                      <span style={{ color: "#50e3c2" }}>+USD {formatCurrency(netInterest)}</span>
                    </div>
                  </div>
                </div>
              )}

              {totalCostData.isMember && shares > 0 && (
                <div style={{ 
                  display: "flex", flexDirection: "column", gap: "4px",
                  borderTop: "1px dashed rgba(80, 227, 194, 0.2)", marginTop: "8px", paddingTop: "8px" 
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.7rem" }}>
                    <span style={{ color: "#888" }}>SUBTOTAL:</span>
                    <span style={{ color: "#ccc", textDecoration: "line-through" }}>
                      {denomination} {formatCurrency(totalCostData.subtotal)}
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.7rem" }}>
                    <span style={{ color: "#50e3c2" }}>DISCOUNT:</span>
                    <span style={{ color: "#50e3c2" }}>
                      -3% ({denomination} {formatCurrency(totalCostData.discountAmount)})
                    </span>
                  </div>
                </div>
              )}

              <div style={{ 
                display: "flex", justifyContent: "space-between", fontSize: "0.7rem", 
                borderTop: "1px solid #222", paddingTop: "8px", marginTop: "8px" 
              }}>
                <span style={{ color: "#888" }}>TOTAL DUE:</span>
                <span style={{ color: amountDue > balance ? "#ff7b72" : "#50e3c2", fontWeight: "bold" }}>
                  {denomination} {formatCurrency(amountDue)}
                </span>
              </div>
            </div>

            <div style={{ display: "flex", gap: "10px" }}>
              <button onClick={onClose} style={{ flex: 1, padding: "12px", background: "transparent", border: "1px solid #333", color: "#888", cursor: "pointer", fontFamily: "inherit" }}>CANCEL</button>
              <button 
                onClick={() => setShowConfirm(true)}
                disabled={amountDue > balance || shares < 1 || isProcessing} 
                style={{ 
                  flex: 2, padding: "12px", background: (amountDue > balance || isProcessing) ? "transparent" : "#50e3c2", 
                  border: (amountDue > balance || isProcessing) ? "1px solid #333" : "none", color: (amountDue > balance || isProcessing) ? "#444" : "#000", 
                  fontWeight: "bold", cursor: (amountDue > balance || isProcessing) ? "not-allowed" : "pointer", opacity: isProcessing ? 0.6 : 1
                }}
              >
                {isProcessing ? "PROCESSING..." : (amountDue > balance ? "INSUFFICIENT FUNDS" : "BUY")}
              </button>
            </div>

            {showConfirm && (
              <div style={{
                position: "absolute", inset: 0, background: "rgba(0, 20, 20, 0.98)", 
                display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", 
                padding: "24px", border: "2px solid #50e3c2", textAlign: "center", zIndex: 10
              }}>
                <div style={{ color: "#fff", marginBottom: "20px", fontSize: "0.85rem", lineHeight: "1.6" }}>
                  Purchasing <span style={{color: '#50e3c2'}}>{shares.toLocaleString()}</span> {unitLabel}<br/>
                  Ticker: {companyState.companyTicker}<br/>
                  Cost: <span style={{color: '#50e3c2'}}>{denomination} {formatCurrency(amountDue, denomination)}</span>
                  {totalCostData.isMember && (
                    <div style={{ color: "#50e3c2", fontSize: "0.65rem", marginTop: "5px" }}>
                      Includes 3% Corporate Discount
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: "10px", width: "100%" }}>
                  <button onClick={() => setShowConfirm(false)} style={{ flex: 1, padding: "10px", background: "rgba(255,255,255,0.1)", color: "#fff", border: "none", fontFamily: "inherit", cursor: "pointer" }}>CANCEL</button>
                  <button 
                    onClick={handleTransactionBuy} 
                    disabled={isProcessing}
                    style={{ 
                      flex: 1, padding: "10px", background: isProcessing ? "#2a7a69" : "#50e3c2",
                      color: "#000", border: "none", fontWeight: "bold", fontFamily: "inherit", cursor: isProcessing ? "not-allowed" : "pointer" 
                    }}
                  >
                    {isProcessing ? "PROCESSING..." : "CONFIRM"}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  );
}